import { getReaderBaseUrl } from "../shared/reader-url";
import type { ReaderReportType } from "./report-type";
import { slugifyText } from "./slugger";

/**
 * Channel identifiers for UTM parameter configuration.
 * Each channel has predefined UTM settings.
 */
export type DeepLinkChannel =
  | "telegram"
  | "email"
  | "twitter"
  | "agent"
  | "internal";

/**
 * Base options for building report deep links.
 */
export interface DeepLinkOptions {
  reportDate: string;
  reportType: ReaderReportType;
  locale?: string;
  channel?: DeepLinkChannel;
  utmContent?: string;
  baseUrl?: string;
}

/**
 * Options for building topic deep links.
 */
export interface TopicDeepLinkOptions extends DeepLinkOptions {
  topicTitle: string;
  /** Optional pre-computed slug (use when duplicate handling via buildReportSlugMaps is needed) */
  slug?: string;
}

/**
 * Options for building Explore category deep links.
 *
 * Targets the live route `${baseUrl}/${reportType}/${categorySlug}` —
 * mirror of the canonical URL emitted by
 * `src/app/(site)/[lang]/[reportType]/explore/[category]/page.tsx`.
 */
export interface ExploreCategoryDeepLinkOptions {
  reportType: ReaderReportType;
  categorySlug: string;
  channel?: DeepLinkChannel;
  utmContent?: string;
  baseUrl?: string;
  reportDate?: string;
}

/**
 * Get UTM parameters for a specific channel.
 * Returns null for channels that don't use UTM (agent, internal).
 */
export function getUtmParams({
  channel,
  reportDate,
  content,
}: {
  channel: DeepLinkChannel;
  reportDate?: string;
  content?: string;
}): URLSearchParams | null {
  // biome-ignore lint/nursery/noUnnecessaryConditions: switch on union type is valid, exhaustive check enforced via default
  switch (channel) {
    case "telegram":
      return new URLSearchParams({
        utm_source: "telegram",
        utm_medium: "post",
        utm_campaign: "daily-ai-primer",
        ...(content && { utm_content: content }),
      });

    case "email":
      return new URLSearchParams({
        utm_source: "newsletter",
        utm_medium: "email",
        utm_campaign: reportDate
          ? `daily-ai-primer-${reportDate}`
          : "daily-ai-primer",
        ...(content && { utm_content: content }),
      });

    case "twitter":
      return new URLSearchParams({
        utm_source: "twitter",
        utm_medium: "post",
        utm_campaign: "daily-ai-primer",
        ...(content && { utm_content: content }),
        // Include the edition date so the shared URL is unique per day.
        // X caches link-preview scrapes by URL for ~7 days; if we kept the
        // URL stable, a pre-OG scrape would be served to followers for a
        // week even after we've posted a fresh image.
        ...(reportDate && { utm_term: reportDate }),
      });

    case "agent":
    case "internal":
      return null;
    default: {
      const _exhaustiveCheck: never = channel;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Build the base report path without query params or anchor.
 */
function buildReportPath({
  baseUrl,
  locale,
  reportType,
  reportDate,
}: {
  baseUrl: string;
  locale: string;
  reportType: ReaderReportType;
  reportDate: string;
}): string {
  const normalizedBaseUrl = baseUrl.endsWith("/")
    ? baseUrl.slice(0, -1)
    : baseUrl;
  return `${normalizedBaseUrl}/${locale}/${reportType}/reports/${reportDate}`;
}

/**
 * Build a complete deep link URL with optional UTM params and anchor.
 */
function buildDeepLinkUrl({
  basePath,
  utmParams,
  anchor,
}: {
  basePath: string;
  utmParams: URLSearchParams | null;
  anchor?: string;
}): string {
  const search = utmParams?.toString() ? `?${utmParams.toString()}` : "";
  const hash = anchor ? `#${anchor}` : "";

  // Consistent structure: path?utm#anchor
  return `${basePath}${search}${hash}`;
}

/**
 * Build a deep link to a specific topic in a report.
 * The anchor is generated by slugifying the topic title,
 * unless a pre-computed slug is provided.
 */
export function buildTopicDeepLink({
  reportDate,
  reportType,
  topicTitle,
  slug,
  locale = "en",
  channel = "internal",
  utmContent,
  baseUrl,
}: TopicDeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const basePath = buildReportPath({
    baseUrl: resolvedBaseUrl,
    locale,
    reportType,
    reportDate,
  });

  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });

  const anchor = slug ?? slugifyText(topicTitle);

  return buildDeepLinkUrl({ basePath, utmParams, anchor });
}

/**
 * Build a deep link to the live Explore category page at
 * `${baseUrl}/${reportType}/${categorySlug}`. Mirror of the canonical URL in
 * `src/app/(site)/[lang]/[reportType]/explore/[category]/page.tsx`.
 */
export function buildExploreCategoryDeepLink({
  reportType,
  categorySlug,
  channel = "internal",
  utmContent,
  baseUrl,
  reportDate,
}: ExploreCategoryDeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const normalizedBaseUrl = resolvedBaseUrl.endsWith("/")
    ? resolvedBaseUrl.slice(0, -1)
    : resolvedBaseUrl;
  const basePath = `${normalizedBaseUrl}/${reportType}/${categorySlug}`;

  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });

  return buildDeepLinkUrl({ basePath, utmParams });
}

/**
 * Build a deep link to a report (no anchor).
 */
export function buildReportDeepLink({
  reportDate,
  reportType,
  locale = "en",
  channel = "internal",
  utmContent,
  baseUrl,
}: DeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const basePath = buildReportPath({
    baseUrl: resolvedBaseUrl,
    locale,
    reportType,
    reportDate,
  });

  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });

  return buildDeepLinkUrl({ basePath, utmParams });
}

// ============================================================
// Explore Deep Links
// ============================================================

/**
 * Options for building explore page deep links.
 */
export interface ExploreDeepLinkOptions {
  reportType: ReaderReportType;
  locale?: string;
  channel?: DeepLinkChannel;
  utmContent?: string;
  baseUrl?: string;
  reportDate?: string;
}

/**
 * Options for building individual story deep links.
 */
export interface StoryDeepLinkOptions extends ExploreDeepLinkOptions {
  storyId: string;
}

function getExploreHubPathSegment(reportType: ReaderReportType): string {
  return reportType === "engineer" ? "" : `/${reportType}`;
}

/**
 * Build the canonical explore hub URL for a report type.
 * Engineer hub is served at the site root; creative at `/creative`.
 */
export function buildExploreHubUrl({
  reportType,
  baseUrl,
}: {
  reportType: ReaderReportType;
  baseUrl?: string;
}): string {
  const resolved = baseUrl ?? getReaderBaseUrl();
  const trimmed = resolved.endsWith("/") ? resolved.slice(0, -1) : resolved;
  const segment = getExploreHubPathSegment(reportType);
  if (segment === "") {
    return `${trimmed || ""}/`;
  }
  return `${trimmed}${segment}`;
}

/**
 * Build the base explore path without query params.
 *
 * Unlike the hub (where engineer lives at the site root), nested explore
 * paths such as story pages are only served under the report-type prefix
 * (`/engineer/:path*` and `/creative/:path*` rewrites), so this always
 * includes the report type.
 */
function buildExplorePath({
  baseUrl,
  reportType,
}: {
  baseUrl: string;
  locale?: string;
  reportType: ReaderReportType;
}): string {
  const trimmed = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
  return `${trimmed}/${reportType}`;
}

/**
 * Build a deep link to the explore page.
 */
export function buildExploreDeepLink({
  reportType,
  channel = "internal",
  utmContent,
  baseUrl,
  reportDate,
}: ExploreDeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const basePath = buildExploreHubUrl({
    reportType,
    baseUrl: resolvedBaseUrl,
  });

  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });

  return buildDeepLinkUrl({ basePath, utmParams });
}

/**
 * Options for building brief deep links.
 */
export interface BriefDeepLinkOptions extends ExploreDeepLinkOptions {
  briefId: string;
  /**
   * Report date (YYYY-MM-DD) the brief belongs to. briefId is only unique
   * within a single (reportDate, reportType) edition, so the date must ride
   * along to stop a link from opening a different day's brief that reused
   * the same slug.
   */
  briefDate?: string;
}

/** Query param the explore surfaces read to auto-open a brief dialog. */
export const BRIEF_DEEP_LINK_PARAM = "brief";
/** Query param carrying the brief's report date (disambiguates reused briefIds). */
export const BRIEF_DEEP_LINK_DATE_PARAM = "bd";

/**
 * Build a deep link that opens a specific brief's detail dialog on the
 * explore hub. Consumed client-side (the hub is statically rendered) by
 * `useBriefDeepLink`; briefs live behind dialogs, not routes, so the target
 * is `hub?brief=<briefId>&bd=<reportDate>` rather than a dedicated page.
 */
export function buildBriefDeepLink({
  reportType,
  briefId,
  briefDate,
  channel = "internal",
  utmContent,
  baseUrl,
  reportDate,
}: BriefDeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const basePath = buildExploreHubUrl({
    reportType,
    baseUrl: resolvedBaseUrl,
  });

  const params = new URLSearchParams({ [BRIEF_DEEP_LINK_PARAM]: briefId });
  if (briefDate) {
    params.set(BRIEF_DEEP_LINK_DATE_PARAM, briefDate);
  }
  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });
  if (utmParams) {
    for (const [key, value] of utmParams) {
      params.set(key, value);
    }
  }

  return buildDeepLinkUrl({ basePath, utmParams: params });
}

/**
 * Build a deep link to an individual story page.
 * Story slugs are the storyId themselves.
 */
export function buildStoryDeepLink({
  reportType,
  storyId,
  locale = "en",
  channel = "internal",
  utmContent,
  baseUrl,
  reportDate,
}: StoryDeepLinkOptions): string {
  const resolvedBaseUrl = baseUrl ?? getReaderBaseUrl();
  const basePath = `${buildExplorePath({
    baseUrl: resolvedBaseUrl,
    locale,
    reportType,
  })}/stories/${encodeURIComponent(storyId)}`;

  const utmParams = getUtmParams({
    channel,
    reportDate,
    content: utmContent,
  });

  return buildDeepLinkUrl({ basePath, utmParams });
}
